// src/pages/Admin.js
//
// Admin-only billing overview at /admin. The real security is in the
// admin-stats edge function, which refuses to return data unless the
// caller's email is in its allowlist. This page is a thin renderer
// over that response.
//
// If a non-admin user reaches /admin, the function returns 403 and we
// show a clear "not authorised" message rather than a generic error.

import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import usePageTitle from '../hooks/usePageTitle'

function formatGBP(amount) {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(amount)
}

function formatDate(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

function StatusBadge({ status, tier, cancelAtPeriodEnd, accessEndedAt, trialEndsAt }) {
  const base = {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 12,
    fontSize: '0.78rem',
    fontWeight: 500,
    whiteSpace: 'nowrap',
  }
  if (status === 'active') {
    const label = cancelAtPeriodEnd ? `active (cancelling)` : `active${tier ? ` · ${tier}` : ''}`
    return <span style={{ ...base, background: '#dcfce7', color: '#166534' }}>{label}</span>
  }
  if (status === 'trialing') {
    // Expired trial: subscription_status hasn't moved off 'trialing' (no
    // Stripe transition for self-managed trials), but trial_ends_at is
    // in the past. Surface this distinctly so admins can see who's lapsed
    // without churning through dates.
    const expired = trialEndsAt && new Date(trialEndsAt).getTime() < Date.now()
    if (expired) {
      return <span style={{ ...base, background: '#fee2e2', color: '#991b1b' }}>trial expired</span>
    }
    return <span style={{ ...base, background: '#dbeafe', color: '#1e40af' }}>trial</span>
  }
  if (status === 'canceled') {
    const label = accessEndedAt ? 'closure window' : 'cancelled (in period)'
    return <span style={{ ...base, background: '#fef3c7', color: '#92400e' }}>{label}</span>
  }
  if (status === 'team_member') {
    return <span style={{ ...base, background: '#e0f0f4', color: '#37706f' }}>team member</span>
  }
  if (status === 'removed') {
    return <span style={{ ...base, background: '#fee2e2', color: '#991b1b' }}>removed</span>
  }
  return <span style={{ ...base, background: '#f3f4f6', color: '#374151' }}>{status ?? 'none'}</span>
}

function SortableHeader({ column, label, sortBy, onClick, align = 'left' }) {
  const isActive = sortBy.column === column
  const arrow = isActive ? (sortBy.direction === 'asc' ? '↑' : '↓') : ''
  return (
    <th
      onClick={() => onClick(column)}
      style={{
        padding: '10px 12px',
        textAlign: align,
        cursor: 'pointer',
        userSelect: 'none',
        color: isActive ? 'var(--ink)' : 'var(--ink-muted)',
        fontWeight: isActive ? 700 : 600,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
      <span style={{
        marginLeft: 6,
        color: isActive ? 'var(--accent)' : 'var(--surface-3)',
        fontSize: '0.85em',
      }}>
        {arrow || '↕'}
      </span>
    </th>
  )
}

function SectionHeading({ children }) {
  return (
    <div style={{
      fontSize: '0.78rem',
      fontWeight: 600,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: 'var(--ink-faint)',
      marginBottom: 12,
      marginTop: 8,
    }}>
      {children}
    </div>
  )
}

// Hero card for the headline number (MRR). Larger, brand-tinted background,
// breakdown rows beneath. Designed to stand out from the lifecycle cards
// without being garish.
function HeroCard({ headlineLabel, headlineValue, headlineSub, breakdown }) {
  return (
    <div className="card" style={{
      padding: 0,
      overflow: 'hidden',
      borderRadius: 12,
    }}>
      <div style={{
        background: 'linear-gradient(135deg, #489291 0%, #37706f 100%)',
        padding: '28px 32px',
        color: 'white',
      }}>
        <div style={{
          fontSize: '0.85rem',
          color: 'rgba(255,255,255,0.85)',
          marginBottom: 8,
          letterSpacing: '0.02em',
        }}>
          {headlineLabel}
        </div>
        <div style={{
          fontSize: '2.6rem',
          fontWeight: 700,
          lineHeight: 1.1,
          fontFamily: 'var(--font-display, "Trebuchet MS", sans-serif)',
        }}>
          {headlineValue}
        </div>
        {headlineSub && (
          <div style={{
            fontSize: '0.88rem',
            color: 'rgba(255,255,255,0.85)',
            marginTop: 6,
          }}>
            {headlineSub}
          </div>
        )}
      </div>
      {breakdown && breakdown.length > 0 && (
        <div style={{ padding: '16px 24px', background: '#fafbfc' }}>
          {breakdown.map((row, i) => (
            <div key={i} style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              padding: '8px 0',
              borderBottom: i < breakdown.length - 1 ? '1px solid var(--surface-2)' : 'none',
              fontSize: '0.92rem',
            }}>
              <span style={{ color: 'var(--ink-muted)' }}>{row.label}</span>
              <span style={{ color: 'var(--ink)' }}>
                <strong>{row.count}</strong>
                {row.amount !== undefined && (
                  <span style={{ color: 'var(--ink-faint)', marginLeft: 10 }}>{row.amount}</span>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// Lifecycle card. Compact, clean, with optional alert state.
function LifecycleCard({ label, value, sub, alert }) {
  return (
    <div className="card" style={{
      padding: '18px 20px',
      borderRadius: 12,
      borderTop: alert ? '3px solid #dc2626' : '3px solid transparent',
      transition: 'transform 0.15s ease',
    }}>
      <div style={{
        fontSize: '0.78rem',
        color: 'var(--ink-muted)',
        marginBottom: 8,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        fontWeight: 500,
      }}>
        {label}
      </div>
      <div style={{
        fontSize: '2rem',
        fontWeight: 700,
        color: alert ? '#dc2626' : 'var(--ink)',
        lineHeight: 1.1,
        fontFamily: 'var(--font-display, "Trebuchet MS", sans-serif)',
      }}>
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: '0.8rem', color: 'var(--ink-muted)', marginTop: 6 }}>
          {sub}
        </div>
      )}
    </div>
  )
}

export default function Admin() {
  const { user, loading: authLoading } = useAuth()
  usePageTitle('Admin')
  const navigate = useNavigate()

  const handleSignOut = async () => {
    await supabase.auth.signOut()
    navigate('/login')
  }
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [sortBy, setSortBy] = useState({ column: 'status', direction: 'asc' })
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')

  useEffect(() => {
    if (authLoading) return
    if (!user) {
      setLoading(false)
      setError('not_signed_in')
      return
    }
    const fetch = async () => {
      try {
        const { data: result, error: fnErr } = await supabase.functions.invoke('admin-stats')
        if (fnErr) {
          if (fnErr.message?.includes('403') || fnErr.context?.status === 403) {
            setError('not_authorised')
          } else {
            setError(fnErr.message ?? 'Unknown error')
          }
          return
        }
        // The function returns { error: '...' } with appropriate status for
        // 401/403, which supabase.functions.invoke surfaces as fnErr above.
        // Belt-and-braces:
        if (result?.error) {
          setError(result.error === 'Not authorised' ? 'not_authorised' : result.error)
          return
        }
        setData(result)
      } catch (e) {
        setError(e.message)
      } finally {
        setLoading(false)
      }
    }
    fetch()
  }, [user, authLoading])

  const sortedAccounts = useMemo(() => {
    if (!data?.accounts) return []

    let arr = data.accounts

    // 1. Filter by status. 'all' shows everything. 'canceled_in_period' adds
    // the period_ends_at check, 'closure' checks access_ended_at.
    if (statusFilter !== 'all') {
      const now = Date.now()
      arr = arr.filter(a => {
        switch (statusFilter) {
          case 'active':
            return a.subscription_status === 'active'
          case 'trialing':
            // Only show live (unexpired) trials here. Expired trials get
            // their own chip below.
            return a.subscription_status === 'trialing'
                && (!a.trial_ends_at || new Date(a.trial_ends_at).getTime() >= now)
          case 'trial_expired':
            return a.subscription_status === 'trialing'
                && a.trial_ends_at
                && new Date(a.trial_ends_at).getTime() < now
          case 'canceled_in_period':
            return a.subscription_status === 'canceled'
                && a.current_period_ends_at
                && new Date(a.current_period_ends_at).getTime() > now
                && !a.access_ended_at
          case 'closure':
            return a.access_ended_at != null
          case 'team_member':
            return a.subscription_status === 'team_member'
          default:
            return true
        }
      })
    }

    // 2. Filter by search term. Matches across own email/practice/name AND
    // (for team members) the practice they belong to + that owner's email.
    const term = searchTerm.trim().toLowerCase()
    if (term) {
      arr = arr.filter(a => {
        const haystack = [
          a.email,
          a.practice_name,
          a.full_name,
          a.belongs_to_practice_name,
          a.belongs_to_owner_email,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
        return haystack.includes(term)
      })
    } else {
      arr = [...arr]  // copy so the sort below doesn't mutate state
    }

    // 3. Sort by clicked column. Direction asc/desc.
    const dir = sortBy.direction === 'desc' ? -1 : 1
    const statusOrder = { active: 0, trialing: 1, canceled: 2, team_member: 3, removed: 4 }

    const compareDates = (a, b) => {
      if (a == null && b == null) return 0
      if (a == null) return 1
      if (b == null) return -1
      return new Date(a) - new Date(b)
    }

    const compareNumbers = (a, b) => {
      if (a == null && b == null) return 0
      if (a == null) return 1
      if (b == null) return -1
      return a - b
    }

    const compareStrings = (a, b) => {
      const sa = (a ?? '').toLowerCase()
      const sb = (b ?? '').toLowerCase()
      return sa.localeCompare(sb)
    }

    arr.sort((a, b) => {
      let cmp = 0
      switch (sortBy.column) {
        case 'email':
          cmp = compareStrings(a.email, b.email)
          break
        case 'practice':
          cmp = compareStrings(
            a.practice_name ?? a.belongs_to_practice_name,
            b.practice_name ?? b.belongs_to_practice_name,
          )
          break
        case 'status':
          cmp = (statusOrder[a.subscription_status] ?? 99) - (statusOrder[b.subscription_status] ?? 99)
          if (cmp === 0) cmp = compareDates(a.created_at, b.created_at) * -1
          break
        case 'clients':
          cmp = compareNumbers(a.client_count_live, b.client_count_live)
          break
        case 'team':
          cmp = compareNumbers(a.team_member_count, b.team_member_count)
          break
        case 'ends': {
          const aEnds = a.subscription_status === 'trialing' ? a.trial_ends_at : a.current_period_ends_at
          const bEnds = b.subscription_status === 'trialing' ? b.trial_ends_at : b.current_period_ends_at
          cmp = compareDates(aEnds, bEnds)
          break
        }
        case 'days':
          cmp = compareNumbers(a.days_remaining, b.days_remaining)
          break
        case 'created':
          cmp = compareDates(a.created_at, b.created_at)
          break
        default:
          cmp = 0
      }
      return cmp * dir
    })

    return arr
  }, [data, sortBy, searchTerm, statusFilter])

  // Per-status counts for the filter chips. Calculated from the unfiltered
  // data so chips always show the true breakdown.
  const statusCounts = useMemo(() => {
    if (!data?.accounts) return { all: 0, active: 0, trialing: 0, trial_expired: 0, canceled_in_period: 0, closure: 0, team_member: 0 }
    const now = Date.now()
    const counts = { all: data.accounts.length, active: 0, trialing: 0, trial_expired: 0, canceled_in_period: 0, closure: 0, team_member: 0 }
    for (const a of data.accounts) {
      if (a.subscription_status === 'active') counts.active++
      else if (a.subscription_status === 'trialing') {
        // Split trialing into live vs expired so admins can act on
        // the lapsed ones. An expired trial here means the row is
        // still flagged 'trialing' in the DB but trial_ends_at is past.
        if (a.trial_ends_at && new Date(a.trial_ends_at).getTime() < now) {
          counts.trial_expired++
        } else {
          counts.trialing++
        }
      }
      else if (a.subscription_status === 'team_member') counts.team_member++
      else if (a.subscription_status === 'canceled'
            && a.current_period_ends_at
            && new Date(a.current_period_ends_at).getTime() > now
            && !a.access_ended_at) {
        counts.canceled_in_period++
      }
      if (a.access_ended_at) counts.closure++
    }
    return counts
  }, [data])

  // Click a column header to sort by it. Same column twice toggles direction.
  const onColumnClick = (column) => {
    setSortBy(prev => prev.column === column
      ? { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
      : { column, direction: 'asc' }
    )
  }

  if (loading || authLoading) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <div className="spinner" />
      </div>
    )
  }

  if (error === 'not_signed_in') {
    return (
      <div style={{ padding: 40, maxWidth: 480, margin: '40px auto', textAlign: 'center' }}>
        <h2 style={{ marginBottom: 8 }}>Sign in required</h2>
        <p style={{ color: 'var(--ink-muted)' }}>You need to sign in to view this page.</p>
      </div>
    )
  }

  if (error === 'not_authorised') {
    return (
      <div style={{ padding: 40, maxWidth: 480, margin: '40px auto', textAlign: 'center' }}>
        <h2 style={{ marginBottom: 8 }}>Not authorised</h2>
        <p style={{ color: 'var(--ink-muted)', marginBottom: 20 }}>This page is for billing admins only.</p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <Link to="/dashboard" className="btn btn-primary">Go to dashboard</Link>
          <button onClick={handleSignOut} className="btn btn-secondary">Sign out</button>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ padding: 40, maxWidth: 600, margin: '40px auto' }}>
        <h2>Could not load admin stats</h2>
        <pre style={{ background: 'var(--surface-2)', padding: 12, borderRadius: 6, fontSize: '0.85rem' }}>{error}</pre>
      </div>
    )
  }

  const { stats, accounts, generated_at } = data

  return (
    <div style={{ padding: '32px 24px', maxWidth: 1280, margin: '0 auto' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 24,
        gap: 16,
        flexWrap: 'wrap',
      }}>
        <div>
          <h1 style={{ margin: 0 }}>Billing admin</h1>
          <span style={{ fontSize: '0.82rem', color: 'var(--ink-faint)' }}>
            As of {new Date(generated_at).toLocaleString('en-GB')}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Link to="/dashboard" className="btn btn-secondary">Dashboard</Link>
          <button onClick={handleSignOut} className="btn btn-secondary">Sign out</button>
        </div>
      </div>

      {/* Drift alerts */}
      {(stats.drift_active_no_period > 0 || stats.drift_active_past_period > 0) && (
        <div className="card" style={{
          marginBottom: 24,
          background: '#fef3c7',
          borderLeft: '4px solid #d97706',
          padding: '16px 20px',
        }}>
          <strong>⚠️ Subscription state drift detected</strong>
          {stats.drift_active_no_period > 0 && (
            <div style={{ marginTop: 6 }}>
              <strong>{stats.drift_active_no_period}</strong> active account(s) with no period end recorded.
              Webhook may have missed the `subscription.created` event.
            </div>
          )}
          {stats.drift_active_past_period > 0 && (
            <div style={{ marginTop: 6 }}>
              <strong>{stats.drift_active_past_period}</strong> active account(s) past their period end.
              Webhook may have missed the `subscription.deleted` event. hasAccess will block them, but
              the row needs cleanup.
            </div>
          )}
        </div>
      )}

      {/* Revenue section ─ hero card */}
      <SectionHeading>Revenue</SectionHeading>
      <div style={{ marginBottom: 32 }}>
        <HeroCard
          headlineLabel="Estimated monthly recurring revenue"
          headlineValue={formatGBP(stats.total_estimated_mrr_gbp)}
          headlineSub={`${stats.active_per_client + stats.active_pro} paying account${(stats.active_per_client + stats.active_pro) === 1 ? '' : 's'}`}
          breakdown={[
            {
              label: 'Per-client',
              count: `${stats.active_per_client} subscriber${stats.active_per_client === 1 ? '' : 's'}`,
              amount: `${stats.active_per_client_clients} client${stats.active_per_client_clients === 1 ? '' : 's'} · ${formatGBP(stats.active_per_client_mrr_gbp)}/mo`,
            },
            {
              label: 'Pro',
              count: `${stats.active_pro} subscriber${stats.active_pro === 1 ? '' : 's'}`,
              amount: `${formatGBP(stats.active_pro_mrr_gbp)}/mo`,
            },
          ]}
        />
      </div>

      {/* Lifecycle section ─ smaller cards */}
      <SectionHeading>Lifecycle</SectionHeading>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 16,
        marginBottom: 32,
      }}>
        <LifecycleCard
          label="Trialing"
          value={statusCounts.trialing}
          sub={stats.trialing_ending_soon > 0 ? `${stats.trialing_ending_soon} ending within 7 days` : 'no urgency'}
        />
        <LifecycleCard
          label="Trial expired"
          value={statusCounts.trial_expired}
          sub={statusCounts.trial_expired > 0 ? 'awaiting plan choice' : 'none'}
          alert={statusCounts.trial_expired > 0}
        />
        <LifecycleCard
          label="Cancelled"
          value={stats.canceled_in_period}
          sub="still in paid period"
        />
        <LifecycleCard
          label="90-day closure"
          value={stats.in_closure_window}
          sub="data will be deleted"
          alert={stats.in_closure_window > 0}
        />
      </div>

      {/* Per-account table header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 16, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>
          {(searchTerm.trim() || statusFilter !== 'all')
            ? `${sortedAccounts.length} of ${accounts.length} account${accounts.length === 1 ? '' : 's'}`
            : `All accounts (${accounts.length})`
          }
        </h2>
        <input
          type="search"
          placeholder="Search by email, practice, or name..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          style={{
            padding: '8px 12px',
            fontSize: '0.88rem',
            border: '1px solid var(--surface-3)',
            borderRadius: 6,
            minWidth: 280,
          }}
        />
      </div>

      {/* Status filter chips */}
      <div style={{
        display: 'flex',
        gap: 8,
        flexWrap: 'wrap',
        marginBottom: 16,
      }}>
        {[
          { key: 'all',                label: 'All',           count: statusCounts.all },
          { key: 'active',             label: 'Active',        count: statusCounts.active },
          { key: 'trialing',           label: 'Trialing',      count: statusCounts.trialing },
          { key: 'trial_expired',      label: 'Trial expired', count: statusCounts.trial_expired },
          { key: 'canceled_in_period', label: 'Cancelled',     count: statusCounts.canceled_in_period },
          { key: 'closure',            label: '90-day closure', count: statusCounts.closure },
          { key: 'team_member',        label: 'Team members',  count: statusCounts.team_member },
        ].map(chip => {
          const active = statusFilter === chip.key
          return (
            <button
              key={chip.key}
              onClick={() => setStatusFilter(chip.key)}
              style={{
                padding: '6px 14px',
                borderRadius: 999,
                border: active ? '1px solid var(--accent)' : '1px solid var(--surface-3)',
                background: active ? 'var(--accent)' : 'white',
                color: active ? 'white' : 'var(--ink-muted)',
                fontSize: '0.85rem',
                fontWeight: active ? 600 : 500,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                transition: 'background 0.15s, color 0.15s',
              }}
            >
              {chip.label}
              <span style={{
                background: active ? 'rgba(255,255,255,0.25)' : 'var(--surface-2)',
                color: active ? 'white' : 'var(--ink-muted)',
                borderRadius: 999,
                padding: '1px 8px',
                fontSize: '0.75rem',
                fontWeight: 600,
              }}>
                {chip.count}
              </span>
            </button>
          )
        })}
      </div>

      <div className="card" style={{ padding: 0, overflow: 'auto' }}>
        {sortedAccounts.length === 0 ? (
          <div style={{ padding: '32px 24px', textAlign: 'center', color: 'var(--ink-muted)' }}>
            {searchTerm.trim()
              ? `No accounts match "${searchTerm.trim()}".`
              : 'No accounts in this filter.'
            }
          </div>
        ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
          <thead>
            <tr style={{ background: 'var(--surface-2)', borderBottom: '1px solid var(--surface-3)' }}>
              <SortableHeader column="email"    label="Email"    sortBy={sortBy} onClick={onColumnClick} />
              <SortableHeader column="practice" label="Practice" sortBy={sortBy} onClick={onColumnClick} />
              <SortableHeader column="status"   label="Status"   sortBy={sortBy} onClick={onColumnClick} />
              <SortableHeader column="clients"  label="Clients"  sortBy={sortBy} onClick={onColumnClick} align="right" />
              <SortableHeader column="team"     label="Team"     sortBy={sortBy} onClick={onColumnClick} align="right" />
              <SortableHeader column="ends"     label="Period / trial ends" sortBy={sortBy} onClick={onColumnClick} />
              <SortableHeader column="days"     label="Days left" sortBy={sortBy} onClick={onColumnClick} align="right" />
              <SortableHeader column="created"  label="Created"  sortBy={sortBy} onClick={onColumnClick} />
            </tr>
          </thead>
          <tbody>
            {sortedAccounts.map(a => {
              const endDate = a.subscription_status === 'trialing' ? a.trial_ends_at : a.current_period_ends_at
              return (
                <tr key={a.bookkeeper_id} style={{ borderBottom: '1px solid var(--surface-2)' }}>
                  <td style={{ padding: '10px 12px' }}>
                    {a.email ?? <em style={{ color: 'var(--ink-faint)' }}>none</em>}
                    {a.is_excluded_from_stats && (
                      <span style={{
                        display: 'inline-block',
                        marginLeft: 8,
                        padding: '1px 6px',
                        background: '#e0e7ff',
                        color: '#3730a3',
                        borderRadius: 10,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                      }}>admin</span>
                    )}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    {a.belongs_to_practice_name || a.belongs_to_owner_email ? (
                      <div>
                        <div>{a.belongs_to_practice_name ?? <em style={{ color: 'var(--ink-faint)' }}>practice unnamed</em>}</div>
                        <div style={{ fontSize: '0.78rem', color: 'var(--ink-faint)', marginTop: 2 }}>
                          via {a.belongs_to_owner_email}
                        </div>
                      </div>
                    ) : (
                      a.practice_name ?? <em style={{ color: 'var(--ink-faint)' }}>not set</em>
                    )}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <StatusBadge
                      status={a.subscription_status}
                      tier={a.subscription_tier}
                      cancelAtPeriodEnd={a.cancel_at_period_end}
                      accessEndedAt={a.access_ended_at}
                      trialEndsAt={a.trial_ends_at}
                    />
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                    {a.client_count_live}
                    {a.client_count_stored !== null && a.client_count_stored !== a.client_count_live && (
                      <span style={{ color: 'var(--ink-faint)', marginLeft: 4 }}>
                        ({a.client_count_stored} on Stripe)
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'right' }}>{a.team_member_count}</td>
                  <td style={{ padding: '10px 12px' }}>{formatDate(endDate)}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                    {a.days_remaining !== null ? (
                      <span style={{ color: a.days_remaining < 0 ? '#dc2626' : a.days_remaining <= 7 ? '#d97706' : 'var(--ink)' }}>
                        {a.days_remaining}
                      </span>
                    ) : '-'}
                  </td>
                  <td style={{ padding: '10px 12px' }}>{formatDate(a.created_at)}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
        )}
      </div>
    </div>
  )
}
